import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { kycService } from "../services/kycService";
// @ts-ignore
import KYCReviewModal from "./KYCReviewModal";
import ConfirmationModal from "../common/ConfirmationModal";
import {
  FaCheck,
  FaTimes,
  FaEye,
  FaFileAlt,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaUser,
  FaExclamationTriangle,
} from "react-icons/fa";
import MainHeader from "../components/MainHeader";

type KYCUser = {
  id: string;
  full_name: string;
  email: string;
  mobile?: string;
  kyc_status?: string;
  kyc_documents?: any[];
  created_at: string;
  updated_at?: string;
  kyc_rejection_reason?: string;
  kyc_rejection_notes?: string;
  kyc_rejected_at?: string;
  kyc_rejected_by?: string;
};

type SortField = "created_at" | "full_name" | "email" | "kyc_status";
type SortDirection = "asc" | "desc";

interface KYCApprovalQueueProps {
  onBackToDashboard: () => void;
  onNavigateToUserManagement?: () => void;
}

export default function KYCApprovalQueue({
  onBackToDashboard,
  onNavigateToUserManagement,
}: KYCApprovalQueueProps) {
  const [users, setUsers] = useState<KYCUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage] = useState(1);
  const [perPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<KYCUser | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const allSelected =
    users.length > 0 && users.every((u) => selectedIds.includes(u.id));
  const [showBulkReject, setShowBulkReject] = useState(false);
  const [bulkRejectReason, setBulkRejectReason] = useState("");
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [docTypeFilter, setDocTypeFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [statusFilter, setStatusFilter] = useState("pending");
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  // Confirmation modal states
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showBulkApproveModal, setShowBulkApproveModal] = useState(false);
  const [approvingUser, setApprovingUser] = useState<KYCUser | null>(null);
  const [rejectingUser, setRejectingUser] = useState<KYCUser | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  // Sorting state
  const [sortField, setSortField] = useState<SortField>("created_at");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

  const fetchPendingKYC = async () => {
    setLoading(true);
    try {
      const response = await kycService.getKYCQueue(
        currentPage,
        perPage,
        search,
        docTypeFilter,
        dateFrom,
        dateTo,
        sortField,
        sortDirection,
        statusFilter
      );
      setUsers(response.users);
      setTotal(response.total);
    } catch (err) {
      // Optionally handle error here
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPendingKYC();
    // eslint-disable-next-line
  }, [
    currentPage,
    perPage,
    search,
    docTypeFilter,
    dateFrom,
    dateTo,
    sortField,
    sortDirection,
    statusFilter,
  ]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <FaSort />;
    }
    return sortDirection === "asc" ? <FaSortUp /> : <FaSortDown />;
  };

  const handleReview = (user: KYCUser) => {
    setSelectedUser(user);
    setShowReviewModal(true);
  };

  const closeReviewModal = () => {
    setShowReviewModal(false);
    setSelectedUser(null);
    fetchPendingKYC(); // Refresh after review
  };

  const toggleSelectAll = () => {
    if (allSelected) setSelectedIds([]);
    else setSelectedIds(users.map((u) => u.id));
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((ids) =>
      ids.includes(id) ? ids.filter((i) => i !== id) : [...ids, id]
    );
  };

  const clearSelection = () => setSelectedIds([]);

  const handleBulkApprove = async () => {
    if (selectedIds.length === 0) return;
    setBulkActionLoading(true);
    for (const id of selectedIds) {
      try {
        await kycService.approveKYC(id);
      } catch {
        // Handle error silently or log it
      }
    }
    setBulkActionLoading(false);
    setSelectedIds([]);
    setShowBulkApproveModal(false);
    fetchPendingKYC();
  };

  const handleApproveClick = (user: KYCUser) => {
    setApprovingUser(user);
    setShowApproveModal(true);
  };

  const handleRejectClick = (user: KYCUser) => {
    setRejectingUser(user);
    setRejectReason("");
    setShowRejectModal(true);
  };

  const handleBulkApproveClick = () => {
    if (selectedIds.length === 0) return;
    setShowBulkApproveModal(true);
  };

  const handleConfirmApprove = async () => {
    if (!approvingUser) return;

    try {
      setActionLoading(true);
      await kycService.approveKYC(approvingUser.id);
      setShowApproveModal(false);
      setApprovingUser(null);
      fetchPendingKYC();
    } catch (error) {
      console.error("Error approving KYC:", error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleConfirmReject = async () => {
    if (!rejectingUser || !rejectReason.trim()) return;

    try {
      setActionLoading(true);
      await kycService.rejectKYC(rejectingUser.id, rejectReason);
      setShowRejectModal(false);
      setRejectingUser(null);
      setRejectReason("");
      fetchPendingKYC();
    } catch (error) {
      console.error("Error rejecting KYC:", error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleCloseApproveModal = () => {
    setShowApproveModal(false);
    setApprovingUser(null);
  };

  const handleCloseRejectModal = () => {
    setShowRejectModal(false);
    setRejectingUser(null);
    setRejectReason("");
  };

  const handleCloseBulkApproveModal = () => {
    setShowBulkApproveModal(false);
  };

  const handleBulkReject = async () => {
    if (selectedIds.length === 0 || !bulkRejectReason) return;
    setBulkActionLoading(true);
    for (const id of selectedIds) {
      try {
        await kycService.rejectKYC(id, bulkRejectReason);
      } catch {
        // Handle error silently or log it
      }
    }
    setBulkActionLoading(false);
    setShowBulkReject(false);
    setBulkRejectReason("");
    setSelectedIds([]);
    fetchPendingKYC();
  };

  const handleViewUser = (user: KYCUser) => {
    if (onNavigateToUserManagement) {
      localStorage.setItem("highlightUserId", user.id);
      onNavigateToUserManagement();
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      pending: { color: "bg-yellow-100 text-yellow-800", text: "Pending" },
      verified: { color: "bg-green-100 text-green-800", text: "Verified" },
      rejected: { color: "bg-red-100 text-red-800", text: "Rejected" },
    };
    const config =
      statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;
    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}
      >
        {config.text}
      </span>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <motion.div
      className="min-h-screen bg-gray-50"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      {/* Main Header */}
      <MainHeader onBackToDashboard={onBackToDashboard} />

      <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {/* Page Header */}
        <motion.div
          className="mb-8"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex items-center justify-between">
            <div>
              <motion.button
                onClick={onBackToDashboard}
                className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4 transition-colors duration-200"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <svg
                  className="w-4 h-4 mr-1"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 19l-7-7m0 0l7-7m-7 7h18"
                  />
                </svg>
                Back to Dashboard
              </motion.button>
              <motion.h1
                className="text-3xl font-bold text-gray-900"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                KYC Management
              </motion.h1>
              <motion.p
                className="mt-2 text-sm text-gray-600"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
              >
                Review and manage KYC verification requests
              </motion.p>
            </div>
          </div>
        </motion.div>

        <motion.div
          className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
        >
          <div className="px-6 py-4 border-b border-gray-200">
            <motion.h3
              className="text-lg font-medium text-gray-900"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.6 }}
            >
              Filters
            </motion.h3>
          </div>
          <div className="px-6 py-4">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div>
                <label
                  htmlFor="search"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Search
                </label>
                <input
                  type="text"
                  id="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by name or email..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div>
                <label
                  htmlFor="status-filter"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Status
                </label>
                <select
                  id="status-filter"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="pending">Pending</option>
                  <option value="rejected">Rejected</option>
                  <option value="verified">Verified</option>
                  <option value="all">All</option>
                </select>
              </div>
              <div>
                <label
                  htmlFor="date-from"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Date From
                </label>
                <input
                  type="date"
                  id="date-from"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div>
                <label
                  htmlFor="date-to"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Date To
                </label>
                <input
                  type="date"
                  id="date-to"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div className="flex items-end">
                <button
                  onClick={() => {
                    setSearch("");
                    setDocTypeFilter("");
                    setDateFrom("");
                    setDateTo("");
                    setStatusFilter("pending");
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Clear Filters
                </button>
              </div>
            </div>
          </div>
        </motion.div>

        {selectedIds.length > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <span className="text-sm font-medium text-blue-800">
                  {selectedIds.length} user(s) selected
                </span>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={handleBulkApproveClick}
                  disabled={bulkActionLoading}
                  className="bg-green-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-green-700 disabled:opacity-50"
                >
                  Bulk Approve
                </button>
                <button
                  onClick={() => setShowBulkReject(true)}
                  disabled={bulkActionLoading}
                  className="bg-red-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-red-700 disabled:opacity-50"
                >
                  Bulk Reject
                </button>
                <button
                  onClick={clearSelection}
                  className="bg-gray-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-700"
                >
                  Clear Selection
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">
              KYC Queue ({total} total)
            </h3>
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
          ) : !users || users.length === 0 ? (
            <div className="px-6 py-12 text-center">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"
                />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                No KYC requests found
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                No KYC requests match your current filters.
              </p>
            </div>
          ) : (
            <>
              <motion.div
                className="overflow-x-auto"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.7 }}
              >
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <input
                          type="checkbox"
                          checked={allSelected}
                          onChange={toggleSelectAll}
                          className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <button
                          onClick={() => handleSort("full_name")}
                          className="flex items-center space-x-1 hover:text-gray-700"
                        >
                          <span>User</span>
                          {getSortIcon("full_name")}
                        </button>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <button
                          onClick={() => handleSort("kyc_status")}
                          className="flex items-center space-x-1 hover:text-gray-700"
                        >
                          <span>Status</span>
                          {getSortIcon("kyc_status")}
                        </button>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Documents
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <button
                          onClick={() => handleSort("created_at")}
                          className="flex items-center space-x-1 hover:text-gray-700"
                        >
                          <span>Submitted</span>
                          {getSortIcon("created_at")}
                        </button>
                      </th>
                      {statusFilter === "rejected" && (
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Rejection Details
                        </th>
                      )}
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {users.map((user, index) => (
                      <motion.tr
                        key={user.id}
                        className="hover:bg-gray-50 transition-colors duration-150"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: index * 0.05 }}
                        whileHover={{
                          scale: 1.01,
                          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                          transition: { duration: 0.2 },
                        }}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(user.id)}
                            onChange={() => toggleSelect(user.id)}
                            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              <div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center">
                                <span className="text-sm font-medium text-indigo-600">
                                  {user.full_name?.charAt(0) || "U"}
                                </span>
                              </div>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {user.full_name}
                              </div>
                              <div className="text-sm text-gray-500">
                                {user.email}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getStatusBadge(user.kyc_status || "pending")}
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span className="inline-flex items-center gap-1">
                            <FaFileAlt className="text-gray-400" />
                            {user.kyc_documents ? user.kyc_documents.length : 0}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {user.created_at
                            ? formatDate(user.created_at)
                            : "N/A"}
                        </td>
                        {statusFilter === "rejected" && (
                          <td className="px-6 py-4 whitespace-nowrap">
                            {user.kyc_rejection_reason && (
                              <div className="text-sm">
                                <div className="font-medium text-red-600 flex items-center gap-1">
                                  <FaExclamationTriangle className="w-3 h-3" />
                                  {user.kyc_rejection_reason}
                                </div>
                                {user.kyc_rejection_notes && (
                                  <div className="text-xs text-gray-500 mt-1">
                                    Notes: {user.kyc_rejection_notes}
                                  </div>
                                )}
                                {user.kyc_rejected_at && (
                                  <div className="text-xs text-gray-400 mt-1">
                                    Rejected on:{" "}
                                    {formatDate(user.kyc_rejected_at)}
                                  </div>
                                )}
                              </div>
                            )}
                          </td>
                        )}
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-2">
                            <motion.button
                              className="text-blue-600 hover:text-blue-800"
                              onClick={() => handleReview(user)}
                              title="Review Documents"
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                            >
                              <FaEye />
                            </motion.button>
                            {onNavigateToUserManagement && (
                              <motion.button
                                className="text-green-600 hover:text-green-800"
                                onClick={() => handleViewUser(user)}
                                title="View User Profile"
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                              >
                                <FaUser />
                              </motion.button>
                            )}
                            {user.kyc_status === "pending" && (
                              <>
                                <motion.button
                                  className="text-green-600 hover:text-green-800"
                                  onClick={() => handleApproveClick(user)}
                                  title="Approve"
                                  whileHover={{ scale: 1.1 }}
                                  whileTap={{ scale: 0.9 }}
                                >
                                  <FaCheck />
                                </motion.button>
                                <motion.button
                                  className="text-red-600 hover:text-red-800"
                                  onClick={() => handleRejectClick(user)}
                                  title="Reject"
                                  whileHover={{ scale: 1.1 }}
                                  whileTap={{ scale: 0.9 }}
                                >
                                  <FaTimes />
                                </motion.button>
                              </>
                            )}
                          </div>
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </motion.div>
            </>
          )}
        </div>

        {/* Bulk Reject Confirmation Modal */}
        <ConfirmationModal
          isOpen={showBulkReject}
          onConfirm={handleBulkReject}
          onCancel={() => setShowBulkReject(false)}
          title="Bulk Reject KYC Verification"
          message={`Are you sure you want to reject KYC verification for ${selectedIds.length} selected user(s)?`}
          confirmText="Reject All"
          confirmButtonColor="bg-red-600 hover:bg-red-700"
          loading={bulkActionLoading}
          showReasonInput={true}
          reasonValue={bulkRejectReason}
          onReasonChange={setBulkRejectReason}
          reasonPlaceholder="Enter rejection reason..."
          reasonRequired={true}
        />

        {showReviewModal && selectedUser && (
          <KYCReviewModal user={selectedUser} onClose={closeReviewModal} />
        )}

        {/* Approve Confirmation Modal */}
        <ConfirmationModal
          isOpen={showApproveModal}
          onConfirm={handleConfirmApprove}
          onCancel={handleCloseApproveModal}
          title="Approve KYC Verification"
          message={`Are you sure you want to approve KYC verification for ${approvingUser?.full_name}?`}
          confirmText="Approve"
          confirmButtonColor="bg-green-600 hover:bg-green-700"
          loading={actionLoading}
        />

        {/* Reject Confirmation Modal */}
        <ConfirmationModal
          isOpen={showRejectModal}
          onConfirm={handleConfirmReject}
          onCancel={handleCloseRejectModal}
          title="Reject KYC Verification"
          message={`Are you sure you want to reject KYC verification for ${rejectingUser?.full_name}?`}
          confirmText="Reject"
          confirmButtonColor="bg-red-600 hover:bg-red-700"
          loading={actionLoading}
          showReasonInput={true}
          reasonValue={rejectReason}
          onReasonChange={setRejectReason}
          reasonPlaceholder="Enter rejection reason..."
          reasonRequired={true}
        />

        {/* Bulk Approve Confirmation Modal */}
        <ConfirmationModal
          isOpen={showBulkApproveModal}
          onConfirm={handleBulkApprove}
          onCancel={handleCloseBulkApproveModal}
          title="Bulk Approve KYC Verification"
          message={`Are you sure you want to approve KYC verification for ${selectedIds.length} selected user(s)?`}
          confirmText="Approve All"
          confirmButtonColor="bg-green-600 hover:bg-green-700"
          loading={bulkActionLoading}
        />
      </div>
    </motion.div>
  );
}
